import { useAdminSelector } from 'admin/reducers';
import { get, words, capitalize, map } from 'lodash';
import React, { useContext } from 'react';
import type { PopoverProps } from 'antd';
import cn from 'classnames';
import type { StoryMediaPlatform } from 'types/story';
import Tooltip from 'admin/components/common/Tooltip';
import { COMPONENT_STATES, DEFAULT_MEDIA_QUERY_PLATFORMS } from 'common/constants';
import { CardEditorContext } from 'admin/components/pages/Story/CardEditor/CardEditorContext';
import { MediaQueryIcon } from 'admin/components/common/MediaQueryIcon';
import { selectEditableElement } from 'admin/reducers/card-editor-extra/selectors';
import { selectCurrentPlatform, selectCurrentState } from 'admin/reducers/card-editor/selectors';
import { getStateLabel } from 'admin/components/pages/Story/CardEditor/Inspector/StateSettings';
import { getInheritanceInfo } from 'admin/components/pages/Story/CardEditor/Inspector/utils';
import { useSymbolValueSource } from 'admin/components/pages/Story/CardEditor/Inspector/hooks/use-symbol-value-source';
import { Button } from 'admin/components/pages/Story/CardEditor/Inspector/PropField/Button';
import Text from 'admin/components/common/Text';
import { isSymbol } from 'utils/blocks/symbol';
import cms from 'utils/cms';
import type { FieldConfig } from '../types';
import css from './LabelWithTooltip.scss';

const Content = (props: { field: FieldConfig; defaultPlatform: StoryMediaPlatform }) => {
	const currentMQ = useAdminSelector(selectCurrentPlatform);
	const currentState = useAdminSelector(selectCurrentState);
	const element = useAdminSelector(selectEditableElement);
	const symbolValueInfo = useSymbolValueSource({ field: props.field });

	if (!props.field) {
		return null;
	}

	const inheritanceInfo = getInheritanceInfo({
		inheritance: props.field.inheritance,
		currentMediaQuery: currentMQ,
		currentState,
		propertyName: props.field.name,
		propertyPath: props.field.path,
		hasLocalAssignment: props.field.hasLocalAssignment,
	});

	const state = get(props.field, 'inheritance.state', COMPONENT_STATES.DEFAULT);
	const mq = get(props.field, 'inheritance.mq', props.defaultPlatform);
	const hasInheritance = props.field?.inheritance !== undefined;
	const isInheritedState = hasInheritance && state !== currentState;
	const isInheritedMq = hasInheritance && mq !== currentMQ;

	const canReset = inheritanceInfo.isUniformProp
		? get(element, props.field.valuePath) !== undefined
		: inheritanceInfo.canReset;
	const source = props.field.inheritance?.source;
	const isStyleField = props.field.valuePath.startsWith('uiConfig.componentProps.styles.');
	let sourceLabel = 'Fallback';
	if (source?.kind === 'preset') sourceLabel = `Preset ${source.presetName}`;
	else if (source?.kind === 'local') sourceLabel = 'Local element';

	const isCmsReference = cms.isCollectionReference(props.field.inheritance?.value);

	// Display information about symbol value source (master or instance)
	const showSymbolSourceInfo = isSymbol(element) && (symbolValueInfo.masterValue || symbolValueInfo.instanceValue);

	return (
		<div className={css.content}>
			{canReset && (
				<div className={css.title}>
					<Button
						preset="reset"
						title="Reset value, assigned for a current platform and state."
						className={css.reset}
						onClick={() => {
							const field = document.querySelector(`input[type="hidden"][name="${props.field.path}"]`);
							if (field) (field as HTMLInputElement).click();
						}}
					>
						reset
					</Button>
				</div>
			)}
			<Text size="footnote" className={css.info}>
				<div className={cn(css.row, { [css.inherited]: isInheritedMq })}>
					Platform:
					{inheritanceInfo.isUniformProp ? (
						<span className={css.rowValue}>All</span>
					) : (
						<MediaQueryIcon
							width={18}
							className={css.icon}
							platform={mq}
							defaultPlatform={props.defaultPlatform}
						/>
					)}
				</div>
				<div className={cn(css.row, { [css.inherited]: isInheritedState })}>
					State:
					<span className={css.rowValue}>{inheritanceInfo.isUniformProp ? 'All' : getStateLabel(state)}</span>
				</div>

				{isStyleField && (
					<div className={css.row}>
						Source:
						<span className={css.rowValue}>{sourceLabel}</span>
					</div>
				)}
				{source?.kind === 'preset' && (
					<div className={css.row}>
						Preset platform:
						<span className={css.rowValue}>{source.authoredPlatform}</span>
					</div>
				)}

				{isCmsReference && (
					<div className={cn(css.row)}>
						CMS Reference:
						<span className={css.rowValue}>Yes</span>
					</div>
				)}

				{showSymbolSourceInfo && (
					<div className={cn(css.row)}>
						Symbol value from:
						<span className={css.rowValue}>{symbolValueInfo.instanceValue ? 'Instance' : 'Master'}</span>
					</div>
				)}
			</Text>
		</div>
	);
};

type LabelWithTooltipProps = Partial<PopoverProps> & {
	className?: string;
	field: FieldConfig;
	children?: React.ReactNode;
};

const tooltipProps = {
	align: { offset: [2, 0] },
	placement: 'leftTop' as const,
	stylePreset: 'dark-2' as const,
	stylesContainer: { width: 178 },
	mouseEnterDelay: 0.5,
};

const LabelWithTooltip = ({ field, children, className, trigger, ...popoverProps }: LabelWithTooltipProps) => {
	const { mediaQuery } = useContext(CardEditorContext);
	const defaultPlatform = mediaQuery?.defaultPlatform || DEFAULT_MEDIA_QUERY_PLATFORMS.DESKTOP;

	const formatFieldName = (fieldName: string): string => {
		return map(words(fieldName), capitalize).join(' ');
	};

	const tooltipClassName = cn(className, css.tooltip, {
		[css.isCmsRef]: cms.isCollectionReference(field?.inheritance?.value ?? ''),
	});

	return (
		<Tooltip
			{...tooltipProps}
			className={tooltipClassName}
			content={<Content field={field} defaultPlatform={defaultPlatform} />}
			{...popoverProps}
			trigger={trigger || LabelWithTooltip.trigger.default}
		>
			{children || formatFieldName(field.name)}
		</Tooltip>
	);
};

const trigger: Record<string, PopoverProps['trigger']> = {
	default: ['contextMenu', 'hover'],
	contextMenu: ['contextMenu'],
};

LabelWithTooltip.trigger = trigger;

export { LabelWithTooltip };
